import { AlertFactory, AlertState } from 'alerts/Alert'
import ModalViewModel from 'infra/ModalViewModel'
import Job from 'jobs/Job'
import BookkeepingProgressionStore from 'jobs/bookkeeping/BookkeepingProgressionStore'
import {
  AvailableBookkeepingSoftwareType,
  BookkeepingPlatform,
  BookkeepingSoftware,
  getAvailableBookkeepingSoftwareToken,
} from 'jobs/bookkeeping/BookkeepingSoftware'
import { action, computed, makeObservable, observable, override } from 'mobx'
import { task } from 'mobx-task'

/**
 * The page of the grant access dialog being shown.
 */
export type BookkeepingGrantAccessPage =
  | { type: 'SelectSoftware' }
  | { type: 'Instructions'; platform: BookkeepingPlatform }

/**
 * The software selected by default when the client picks the `Other` platform.
 */
const DEFAULT_OTHER_SOFTWARE: AvailableBookkeepingSoftwareType = 'Freshbooks'

export default class BookkeepingGrantAccessDialogViewModel extends ModalViewModel {
  /**
   * The page being shown.
   */
  @observable page: BookkeepingGrantAccessPage = { type: 'SelectSoftware' }

  /**
   * The software the client selected when their platform is `Other`.
   */
  @observable otherSoftware: AvailableBookkeepingSoftwareType =
    DEFAULT_OTHER_SOFTWARE

  constructor(
    private job: Job,
    private bookkeepingProgressionStore: BookkeepingProgressionStore,
    private newAlert: AlertFactory,
  ) {
    super()
    makeObservable(this)
  }

  /**
   * The Pro the client needs to grant access to.
   */
  @computed
  get pro() {
    return this.job.championProvider?.member
  }

  /**
   * Whether the Pro's details needed to grant access are available.
   */
  @computed
  get hasProDetails() {
    return Boolean(this.pro?.email)
  }

  /**
   * Whether the current user is the client, as opposed to an admin granting
   * access on their behalf.
   */
  @computed
  get isClientPerspective() {
    return this.job.isClientCollaborator
  }

  /**
   * The software the client selected, if they have selected one.
   */
  @computed
  get software(): BookkeepingSoftware | null {
    if (this.page.type !== 'Instructions') {
      return null
    }

    switch (this.page.platform) {
      case 'QuickBooks':
        return { type: 'QuickBooks' }
      case 'Xero':
        return { type: 'Xero' }
      case 'Other':
        return {
          type: 'Other',
          name: getAvailableBookkeepingSoftwareToken(this.otherSoftware),
        }
    }
  }

  /**
   * Shows the dialog, starting over from the software selection.
   */
  @override
  show() {
    this.page = { type: 'SelectSoftware' }
    this.otherSoftware = DEFAULT_OTHER_SOFTWARE
    this.confirmAccessProvided.reset()
    super.show()
  }

  /**
   * Selects the platform and shows its instructions.
   */
  @action.bound
  selectPlatform(platform: BookkeepingPlatform) {
    this.page = { type: 'Instructions', platform }
  }

  /**
   * Goes back to the software selection.
   */
  @action.bound
  goBack() {
    this.page = { type: 'SelectSoftware' }
  }

  /**
   * Copies a given value to the clipboard.
   */
  @action.bound
  async copyToClipboard(value: string) {
    try {
      await navigator.clipboard.writeText(value)

      this.newAlert()
        .withState(AlertState.Success)
        .saying('Copied!', `${value} was copied to the clipboard.`)
        .autoDismiss()
        .show()
    } catch (error) {
      this.newAlert().fromError(error).show()
      console.error(error)
    }
  }

  /**
   * Confirms the client granted their Pro access to their books.
   */
  confirmAccessProvided = task.resolved(async () => {
    const software = this.software

    if (!software || (software.type === 'Other' && !software.name.trim())) {
      this.newAlert()
        .withState(AlertState.Warning)
        .saying(
          'Bookkeeping software is required',
          'Please select the software used for the books before confirming access.',
        )
        .autoDismiss()
        .show()
      return
    }

    try {
      await this.bookkeepingProgressionStore.confirmUnmanagedClientAccess(
        this.job.id,
        software,
      )

      this.close()
    } catch (error) {
      this.newAlert().fromError(error).show()
      console.error(error)
    }
  })
}
